import { HttpService } from './http.service';
import { http, type HttpRequestOptions } from '@/services/http';
import { getDeviceId } from '@/lib/deviceId';
import type {
  MatchEventDto, 
  EventAck, 
  BatchAckResponse, 
  MatchState, 
  FullMatchState,
  UndoRequest,
  EventResponse
} from '@/common/model';

// Added at send time, not stored in the queue: the device sending is the one
// whose queue entered the action.
function withDeviceId(event: MatchEventDto): MatchEventDto {
  return { ...event, deviceId: getDeviceId() };
}

export const MatchEventService = {
  async sendEvent(
    matchId: string,
    event: MatchEventDto,
    options?: Pick<HttpRequestOptions, 'timeoutMs'>,
  ): Promise<EventAck> {
    return http.post<EventAck>(`/matches/${matchId}/events`, withDeviceId(event), options);
  },

  async sendEvents(
    matchId: string,
    events: MatchEventDto[],
    options?: Pick<HttpRequestOptions, 'timeoutMs'>,
  ): Promise<BatchAckResponse> {
    return http.post<BatchAckResponse>(
      `/matches/${matchId}/events/batch`,
      { events: events.map(withDeviceId) },
      options,
    );
  },

  /** `signal` lets a caller bound the wait (live edit of a rally, VOL-221). */
  async undoEvent(
    matchId: string,
    request: UndoRequest,
    options?: { signal?: AbortSignal },
  ): Promise<BatchAckResponse> {
    return http.post<BatchAckResponse>(`/matches/${matchId}/events/undo`, request, options);
  },

  async undoLast(matchId: string, count = 1): Promise<BatchAckResponse> {
    return this.undoEvent(matchId, { undoCount: count });
  },

  /**
   * Retour en arrière jusqu'à une séquence donnée : le serveur défait toutes
   * les actions postérieures. À préférer à `undoLast`, qui obligeait le client
   * à compter les actions — comptage faux dès que la liste reçue était
   * tronquée, laissant des actions en place après le rollback.
   */
  async undoFrom(matchId: string, fromSequence: number): Promise<BatchAckResponse> {
    return this.undoEvent(matchId, { fromSequence });
  },

  /** The server undo is idempotent: retrying after a timeout is safe. */
  async undoByEventId(
    matchId: string,
    eventId: string,
    options?: { signal?: AbortSignal },
  ): Promise<BatchAckResponse> {
    return this.undoEvent(matchId, { targetEventId: eventId }, options);
  },

  async getMatchState(matchId: string): Promise<MatchState> {
    return HttpService.get<MatchState>(`/matches/${matchId}/state`);
  },

  async getFullMatchState(matchId: string, sinceSequence?: number): Promise<FullMatchState> {
    const query = sinceSequence !== undefined ? `?sinceSequence=${sinceSequence}` : '';
    return HttpService.get<FullMatchState>(`/matches/${matchId}/state/full${query}`);
  },

  async getEventsSince(matchId: string, sinceSequence: number, limit = 100): Promise<EventResponse[]> {
    return HttpService.get<EventResponse[]>(
      `/matches/${matchId}/events?sinceSequence=${sinceSequence}&limit=${limit}`
    );
  }
};

